/**
 * The `/build` share token — the `opts` half of `/build?set=<slug>&opts=<token>`.
 *
 * A configuration is shareable because the address bar mirrors it. The token is
 * a comma-separated list of `<slot8>-<opt8>` pairs, each half the first 8 hex
 * characters of the corresponding UUID, resolved against THAT MODEL'S OWN slot
 * list. A model has a few dozen ids, so a prefix collision inside one list is
 * structurally near-impossible — and where it does happen it resolves to the
 * first match rather than throwing or picking at random.
 *
 * Two rules carry the whole feature:
 *
 *  1. **`-x` means "the sender chose None".** Every slot is encoded, including
 *     ones sitting at their default. Without an explicit sentinel, "deselected"
 *     and "absent" are the same token, and a shared link silently re-adds a
 *     paid add-on the sender deliberately turned off. Shortening the token by
 *     omitting defaults would trade that guarantee for a few dozen characters.
 *
 *  2. **`decodeBuild` never throws and never 404s.** This string arrives from a
 *     URL, so hand-edited junk, a truncated paste, an id the catalog no longer
 *     has, or a slot added after the link was made all degrade to the default
 *     for that slot — never to an error page.
 *
 * The fallback is `resolveInitialSelection`, NOT the raw `buildDefaultSelection`
 * it wraps. The raw helper checks `is_default` before `is_required`, so a
 * merchandiser ticking "default" on a paid optional add-on would make every
 * link that omits that slot add it to the recipient's build. That is rule 1's
 * bug arriving through the other door.
 *
 * No `'use client'`: `decodeBuild` runs in the Server Component and
 * `encodeBuild` runs in the island, and `buildClientBoundary.test.ts` pins the
 * island as the only client module on the route. Pure by construction — no
 * React, no `@/lib/*`, no Supabase.
 */
import type { BundleSelection } from '@sifly/shared-ui/bundle'
import { resolveInitialSelection } from './buildSlot'
import type { BuildSlot } from './buildSlot'

/**
 * One token pair. Both halves are hex because they are UUID prefixes; anything
 * else is junk from the address bar and is skipped rather than guessed at.
 *
 * Exactly 8, never fewer. `encodeBuild` always emits 8, so nothing legitimate
 * produces a short prefix — but plenty of things TRUNCATE one, an email client
 * wrapping the link being the common case. Accepting a short prefix resolved it
 * against the first id that started with it: given `a1b2c3d4` ("Standard", +$0)
 * and `a1b2ffff` ("Carbon", +$1,200), a paste that lost the tail quietly showed
 * the recipient a build $1,200 cheaper than the one that was shared, with
 * nothing on screen saying anything had been lost. Rejecting the pair instead
 * degrades that slot to its default — visibly the page's own starting point,
 * not a different build wearing the sender's link.
 */
const PAIR = /^([0-9a-f]{8})-([0-9a-f]{8}|x)$/

/**
 * Ceiling on the `opts` value before it is parsed.
 *
 * Decoding is O(pairs × slots) over a string an attacker fully controls. The
 * platform's own URL limit already bounds it in practice; this states the bound
 * rather than inheriting one, and is an order of magnitude above the ~40 slots
 * (≈700 chars) a real model produces.
 */
const MAX_PARAM = 2048

/** The 8-char key an id is addressed by inside one model's slot list. */
function key(id: string): string {
  return id.toLowerCase().slice(0, 8)
}

/**
 * The `opts` value for a selection. Every slot appears, in slot order, so the
 * token is stable for a given configuration and a deselected add-on is stated
 * rather than implied.
 */
export function encodeBuild(slots: BuildSlot[], selection: BundleSelection): string {
  return slots
    .map((slot) => {
      const optionId = selection[slot.id]
      return `${key(slot.id)}-${optionId ? key(optionId) : 'x'}`
    })
    .join(',')
}

/**
 * A selection from an `opts` value, resolved against this model's slots.
 *
 * Starts from what the page would have shown with no token at all, then applies
 * only the pairs that resolve. Anything unresolvable — a malformed pair, a slot
 * this model does not have, an option that was deleted — is left at that
 * starting value, which is why this can be handed a URL string unguarded.
 */
export function decodeBuild(param: string, slots: BuildSlot[]): BundleSelection {
  const selection = resolveInitialSelection(slots)

  // Typed `string`, but the argument is a raw URL value and the type system is
  // not what is handing it over: Next returns `string[]` for a repeated key, so
  // `?opts=a&opts=b` used to reach `.split` on an array and throw INSIDE the
  // Server Component. There is no `error.tsx` under `app/build`, so that was a
  // hard 500 on a customer-facing route that anyone could trigger by hand. The
  // callers normalise; this coerces anyway, because "never throws" is the whole
  // contract and it must not depend on every caller remembering.
  const value = typeof param === 'string' ? param.slice(0, MAX_PARAM) : ''

  for (const raw of value.split(',')) {
    const pair = PAIR.exec(raw.trim().toLowerCase())
    if (!pair) continue
    const [, slotKey, optionKey] = pair

    // First match wins. With a few dozen ids per model a collision is
    // near-impossible, but "deterministic" beats "correct in the usual case":
    // the same link must decode the same way every time it is opened.
    const slot = slots.find((s) => key(s.id).startsWith(slotKey))
    if (!slot) continue

    // `-x` means "the sender chose None" — but None is only ever a choice on
    // a slot that is NOT required (BuildConfigurator.tsx only renders a
    // "None" card for an optional step/add-on; a required slot never gets
    // one). If a slot was optional when the link was made — hence the `-x`
    // — and has SINCE become required (every `included` slot is required by
    // migration 367's backfill, and an `included` slot renders no
    // OptionCard at all), honouring the sentinel here writes null into a
    // slot the recipient has no control anywhere on the page to answer:
    // Add to cart stays disabled with nothing to click. Leaving the
    // resolved default in place instead — by simply not writing over it —
    // mirrors the unmatched-option branch two lines down, and for the same
    // reason.
    if (optionKey === 'x') {
      if (!slot.is_required) selection[slot.id] = null
      continue
    }

    const option = slot.options.find((o) => key(o.id).startsWith(optionKey))
    // No match means the option is gone from the catalog. Leaving the default
    // in place shows the recipient a buildable configuration; writing null
    // would leave a required slot unanswered and Add to cart disabled.
    if (option) selection[slot.id] = option.id
  }

  return selection
}
